import { useUserStore } from '@/stores/user'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

// Views
import CandidateLoginView from '@/views/candidate/CandidateLoginView.vue'
import CandidateStatusView from '@/views/CandidateStatusView.vue'
import HRLoginView from '@/views/hr/HRLoginView.vue'
import WelcomeView from '@/views/WelcomeView.vue'

// HR Views
import CandidatesManagement from '@/views/hr/CandidatesManagement.vue'
import HRDashboard from '@/views/hr/HRDashboard.vue'
import JobsManagement from '@/views/hr/JobsManagement.vue'
import NeedsManagement from '@/views/hr/NeedsManagement.vue'

// Candidate Views (authentifiés)
import CandidateApplications from '@/views/candidate/CandidateApplications.vue'
import CandidateQuestionnaires from '@/views/candidate/CandidateQuestionnaires.vue'

// Admin Views
import AdminDashboard from '@/views/admin/AdminDashboard.vue'
import ChannelsSettings from '@/views/admin/ChannelsSettings.vue'
import DatabaseSettings from '@/views/admin/DatabaseSettings.vue'
import LLMSettings from '@/views/admin/LLMSettings.vue'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth: boolean
    roles?: string[]
    title?: string
    appMode?: 'candidate' | 'hr' | 'welcome'
  }
}

// Routes pour l'espace candidat
const candidateRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'candidate-home',
    component: CandidateLoginView,
    meta: { requiresAuth: false, title: 'Espace Candidat', appMode: 'candidate' }
  },
  {
    path: '/status',
    name: 'candidate-status',
    component: CandidateStatusView,
    meta: { requiresAuth: false, title: 'Statut de candidature', appMode: 'candidate' }
  },
  // Routes candidat authentifié
  {
    path: '/applications',
    name: 'candidate-applications',
    component: CandidateApplications,
    meta: { requiresAuth: true, roles: ['candidate'], title: 'Mes candidatures', appMode: 'candidate' }
  },
  {
    path: '/questionnaires',
    name: 'candidate-questionnaires',
    component: CandidateQuestionnaires,
    meta: { requiresAuth: true, roles: ['candidate'], title: 'Questionnaires', appMode: 'candidate' }
  }
]

// Routes pour l'espace RH/Admin
const hrRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'hr-login',
    component: HRLoginView,
    meta: { requiresAuth: false, title: 'Connexion Employé', appMode: 'hr' }
  },
  {
    path: '/hr/dashboard',
    name: 'hr-dashboard',
    component: HRDashboard,
    meta: { requiresAuth: true, roles: ['hr'], title: 'Dashboard RH', appMode: 'hr' }
  },
  {
    path: '/hr/needs',
    name: 'hr-needs',
    component: NeedsManagement,
    meta: { requiresAuth: true, roles: ['hr'], title: 'Gestion des besoins', appMode: 'hr' }
  },
  {
    path: '/hr/jobs',
    name: 'hr-jobs',
    component: JobsManagement,
    meta: { requiresAuth: true, roles: ['hr'], title: 'Gestion des postes', appMode: 'hr' }
  },
  {
    path: '/hr/candidates',
    name: 'hr-candidates',
    component: CandidatesManagement,
    meta: { requiresAuth: true, roles: ['hr'], title: 'Gestion des candidats', appMode: 'hr' }
  },
  // Routes candidat authentifié
  {
    path: '/candidate/applications',
    name: 'candidate-applications',
    component: CandidateApplications,
    meta: { requiresAuth: true, roles: ['candidate'], title: 'Mes candidatures', appMode: 'hr' }
  },
  {
    path: '/candidate/questionnaires',
    name: 'candidate-questionnaires',
    component: CandidateQuestionnaires,
    meta: { requiresAuth: true, roles: ['candidate'], title: 'Questionnaires', appMode: 'hr' }
  },
  // Routes Admin
  {
    path: '/admin/dashboard',
    name: 'admin-dashboard',
    component: AdminDashboard,
    meta: { requiresAuth: true, roles: ['admin'], title: 'Administration', appMode: 'hr' }
  },
  {
    path: '/admin/llm',
    name: 'admin-llm',
    component: LLMSettings,
    meta: { requiresAuth: true, roles: ['admin'], title: 'Paramètres LLM', appMode: 'hr' }
  },
  {
    path: '/admin/database',
    name: 'admin-database',
    component: DatabaseSettings,
    meta: { requiresAuth: true, roles: ['admin'], title: 'Base de données', appMode: 'hr' }
  },
  {
    path: '/admin/channels',
    name: 'admin-channels',
    component: ChannelsSettings,
    meta: { requiresAuth: true, roles: ['admin'], title: 'Canaux de publication', appMode: 'hr' }
  }
]

// Routes pour la page d'accueil
const welcomeRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'welcome',
    component: WelcomeView,
    meta: { requiresAuth: false, title: 'Bienvenue', appMode: 'welcome' }
  }
]

// Fonction pour créer le router selon le mode
export const createAppRouter = (mode: 'candidate' | 'hr' | 'welcome') => {
  let routes: RouteRecordRaw[] = []
  let baseURL = '/'

  switch (mode) {
    case 'candidate':
      routes = [...candidateRoutes, { path: '/:pathMatch(.*)*', redirect: '/' }]
      break
    case 'hr':
      routes = [...hrRoutes, { path: '/:pathMatch(.*)*', redirect: '/' }]
      break
    case 'welcome':
    default:
      routes = [...welcomeRoutes, { path: '/:pathMatch(.*)*', redirect: '/' }]
      break
  }

  const router = createRouter({
    history: createWebHistory(baseURL),
    routes
  })

  // Navigation Guards
  router.beforeEach(async (to, _from, next) => {
    // Définir le titre de la page
    if (to.meta.title) {
      document.title = `${to.meta.title} - Recruitment App`
    }

    // Pour les routes HR et Candidat, vérifier l'authentification
    if ((mode === 'hr' || mode === 'candidate') && to.meta.requiresAuth) {
      const userStore = useUserStore()

      if (!userStore.isAuthenticated) {
        next('/')
        return
      }

      // Vérifier les rôles
      if (to.meta.roles && to.meta.roles.length > 0) {
        const hasRequiredRole = to.meta.roles.some((role: string) => userStore.hasRole(role))
        if (!hasRequiredRole) {
          next('/')
          return
        }
      }
    }

    next()
  })

  return router
}

// Export par défaut pour compatibilité
export default createAppRouter('welcome')
